import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";

const NAV_LINKS = [
  { label: "Dashboard", path: "/dashboard", icon: "🏠" },
  { label: "Entries", path: "/entries", icon: "📋" },
  { label: "Settings", path: "/settings", icon: "⚙️" },
];

export default function Toolbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { effectiveTheme, setTheme } = useTheme();

  function handleLogout() {
    logout();
    navigate("/login");
  }

  function toggleTheme() {
    setTheme(effectiveTheme === "dark" ? "light" : "dark");
  }

  return (
    <>
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-sm)",
          padding: "0 var(--spacing-md)",
          height: 52,
          width: "100%",
          boxSizing: "border-box",
          background: "var(--color-surface)",
          borderBottom: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-sm)",
          overflow: "hidden",
        }}
      >
        {/* Brand */}
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "var(--spacing-xs) var(--spacing-sm)",
            borderRadius: "var(--radius-sm)",
            fontWeight: "var(--font-weight-semibold)",
            fontSize: "var(--font-size-md)",
            color: "var(--color-primary)",
            marginRight: "var(--spacing-md)",
            lineHeight: 1,
            flexShrink: 0,
          }}
          title="Go to Dashboard"
        >
          HealthTracker
        </button>

        {/* Desktop nav links — hidden on mobile via CSS class */}
        <nav className="toolbar-nav">
          {NAV_LINKS.map(({ label, path }) => {
            const active = location.pathname === path || location.pathname.startsWith(path + "/");
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                style={{
                  background: active ? "var(--color-primary-light)" : "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "var(--spacing-xs) var(--spacing-sm)",
                  borderRadius: "var(--radius-sm)",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: active ? "var(--font-weight-semibold)" : "var(--font-weight-normal)",
                  color: active ? "var(--color-primary)" : "var(--color-text-secondary)",
                  transition: "background 0.15s, color 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!active)
                    (e.currentTarget as HTMLButtonElement).style.background = "var(--color-hover)";
                }}
                onMouseLeave={(e) => {
                  if (!active) (e.currentTarget as HTMLButtonElement).style.background = "none";
                }}
              >
                {label}
              </button>
            );
          })}
        </nav>

        {/* Right-side actions — hidden on mobile, the bottom nav handles everything */}
        <div
          className="toolbar-actions"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-xs)",
            marginLeft: "auto",
            flexShrink: 0,
          }}
        >
          <button
            onClick={toggleTheme}
            title={`Switch to ${effectiveTheme === "dark" ? "light" : "dark"} mode`}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "var(--spacing-xs)",
              borderRadius: "var(--radius-sm)",
              fontSize: 18,
              lineHeight: 1,
              color: "var(--color-text-secondary)",
            }}
          >
            {effectiveTheme === "dark" ? "☀️" : "🌙"}
          </button>

          {/* Sign out — hidden on mobile, shown on desktop via CSS class */}
          <button
            className="toolbar-signout"
            onClick={handleLogout}
            style={{
              background: "none",
              border: "1px solid var(--color-border)",
              cursor: "pointer",
              padding: "var(--spacing-xs) var(--spacing-sm)",
              borderRadius: "var(--radius-sm)",
              fontSize: "var(--font-size-sm)",
              color: "var(--color-text-secondary)",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "var(--color-hover)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "none";
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* ── Bottom nav — mobile only, shown via CSS ──────────────────────── */}
      <nav className="bottom-nav" aria-label="Main navigation">
        {NAV_LINKS.map(({ label, path, icon }) => {
          const active = location.pathname === path || location.pathname.startsWith(path + "/");
          return (
            <button
              key={path}
              className={`bottom-nav-item${active ? " active" : ""}`}
              onClick={() => navigate(path)}
              aria-current={active ? "page" : undefined}
            >
              <span className="nav-icon" aria-hidden="true">
                {icon}
              </span>
              <span>{label}</span>
            </button>
          );
        })}
        {/* Theme toggle */}
        {/* Sign out */}
        <button className="bottom-nav-item" onClick={handleLogout} aria-label="Sign out">
          <span className="nav-icon" aria-hidden="true">
            🚪
          </span>
          <span>Sign out</span>
        </button>
      </nav>
    </>
  );
}
